// Catalog.ID Client — Device Local Storage
// ============================================================================
// Manages encrypted private keys in localStorage using the split-storage model.
// Keys are encrypted with a deviceEncryptionKey held on the server.
// The device token identifies this device for PIN/unlock operations.
//
// All keys are namespaced by account CID: {cid}_device_token, {cid}_key_attr_encrypt, etc.
// A bootstrap key (cid_active_cid) stores the active CID so that page-refresh
// can find the right partition without querying the server first.

import * as aes from '../crypto/aes';
import { isDeviceStorableKeyId } from '../crypto/keybag';
import {
    clearDeviceKeyManifest, isDeviceKeyChosen, matchesDeviceKeyFamily, readDeviceKeyManifest,
    type DeviceKeyManifest,
} from './device-key-selection';
import { clearKeyUseRecord } from './key-use-record';

const ACTIVE_CID_KEY = 'cid_active_cid';

function deviceTokenKey(cid: string): string { return `${cid}_device_token`; }
function deviceIdKey(cid: string): string { return `${cid}_device_id`; }
function pinLengthKey(cid: string): string { return `${cid}_pin_length`; }
function unlockFactorsKey(cid: string): string { return `${cid}_unlock_factors`; }
function noLocalStorageKey(cid: string): string { return `${cid}_no_local_storage`; }
function keyPrefix(cid: string): string { return `${cid}_key_`; }
/**
 * Namespace of the ENCRYPTED DEVICE SLOTS (`state/device-slots.ts`) — cached
 * Worker tokens and the organization's stored voucher codes
 * (registration-wallets §4.12, D19). Defined here, beside the key namespace it
 * must stay out of, because {@link clearDeviceStorage} has to sweep it and the
 * two prefixes must never be allowed to drift into overlapping.
 */
export function slotPrefix(cid: string): string { return `${cid}_slot_`; }

// ── Active CID bootstrap ────────────────────────────────────────────────────

/** Get the active CID from localStorage (survives page refresh). */
export function getActiveCid(): string | null {
    return localStorage.getItem(ACTIVE_CID_KEY);
}

/** Set the active CID in localStorage. Called at login and identity switch. */
export function setActiveCid(cid: string): void {
    localStorage.setItem(ACTIVE_CID_KEY, cid);
}

/** Clear the active CID. Called at logout. */
export function clearActiveCid(): void {
    localStorage.removeItem(ACTIVE_CID_KEY);
}

// ── Device Token ─────────────────────────────────────────────────────────────

/** Get the stored device token for an account, or null if this is a new device. */
export function getDeviceToken(cid: string): string | null {
    return localStorage.getItem(deviceTokenKey(cid));
}

/** Store the device token for an account. */
export function setDeviceToken(cid: string, token: string): void {
    localStorage.setItem(deviceTokenKey(cid), token);
}

// ── Device ID ────────────────────────────────────────────────────────────────
// The server's row id for THIS device, learned from the setup/reset response.
// It is what lets the Devices pane mark one row "— this device": the token
// can't, because the server never returns tokens in the device list.

/** Get the server-assigned device id for this account on this device. */
export function getDeviceId(cid: string): number | null {
    const raw = localStorage.getItem(deviceIdKey(cid));
    if (raw === null) return null;
    const id = Number(raw);
    return Number.isInteger(id) ? id : null;
}

/** Store the server-assigned device id (from a setup/reset response). */
export function setDeviceId(cid: string, deviceId: number): void {
    localStorage.setItem(deviceIdKey(cid), String(deviceId));
}

/** Forget the device id — always alongside the device token. */
export function clearDeviceId(cid: string): void {
    localStorage.removeItem(deviceIdKey(cid));
}

// ── PIN Length ────────────────────────────────────────────────────────────────

/** Get the stored PIN length for an account, or null if not set. */
export function getPinLength(cid: string): 4 | 6 | 8 | null {
    const val = localStorage.getItem(pinLengthKey(cid));
    if (val === '4' || val === '6' || val === '8') return Number(val) as 4 | 6 | 8;
    return null;
}

/** Store the PIN length for an account. */
export function setPinLength(cid: string, len: 4 | 6 | 8): void {
    localStorage.setItem(pinLengthKey(cid), String(len));
}

// ── Unlock factors ───────────────────────────────────────────────────────────
// Which factors THIS device's row asks for (device-setup-restructure §6.1),
// remembered beside the PIN length for the same reason: the unlock screen has
// to paint before the node has answered. Absent means "no factor" — a *No PIN*
// device — or simply "not known yet". Only `POST /api/device/unlock`'s
// `factors_required` is authoritative; this never overrides it.

/** The four modes of §3, as the two orthogonal factors the member types. */
export type UnlockFactorHint = 'pin' | 'totp' | 'pin+totp';

/** This device's remembered factors, or null when there are none on record. */
export function getUnlockFactors(cid: string): UnlockFactorHint | null {
    const val = localStorage.getItem(unlockFactorsKey(cid));
    return val === 'pin' || val === 'totp' || val === 'pin+totp' ? val : null;
}

/** Remember the factors this device's row now asks for. */
export function setUnlockFactors(cid: string, hint: UnlockFactorHint): void {
    localStorage.setItem(unlockFactorsKey(cid), hint);
}

/** Forget them — the device moved to a mode that asks for nothing. */
export function clearUnlockFactors(cid: string): void {
    localStorage.removeItem(unlockFactorsKey(cid));
}

// ── No Local Storage Mode ────────────────────────────────────────────────────

/** Whether the user chose "no local storage" mode for this account on this device. */
export function isNoLocalStorageMode(cid: string): boolean {
    return localStorage.getItem(noLocalStorageKey(cid)) === 'true';
}

/** Mark this device as "no local storage" mode for an account. */
export function setNoLocalStorageMode(cid: string): void {
    localStorage.setItem(noLocalStorageKey(cid), 'true');
}

/** Clear the "no local storage" flag for an account. */
export function clearNoLocalStorageMode(cid: string): void {
    localStorage.removeItem(noLocalStorageKey(cid));
}

// ── Encrypted Key Storage ────────────────────────────────────────────────────

/** Check if this device has any stored encrypted keys for an account. */
export function hasStoredKeys(cid: string): boolean {
    const prefix = keyPrefix(cid);
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) return true;
    }
    return false;
}

/** Store an encrypted private key in localStorage for an account.
 *  Storage key: {cid}_key_{keyId}@{keyIndex} to support key chains after rotation.
 *  Value format: JSON { enc, alg, idx, st } where enc = encrypted key data,
 *  alg = algorithm, idx = keyIndex, st = status. The algorithm is required —
 *  callers must supply it explicitly so storage stays consistent with the
 *  matching accountpublickey row. */
export function storeEncryptedKey(cid: string, keyId: string, encryptedData: string,
    meta: { algorithm: string; keyIndex?: number; status?: string }): void {
    const idx = meta.keyIndex ?? 0;
    const value = JSON.stringify({
        enc: encryptedData,
        alg: meta.algorithm,
        idx,
        st: meta.status ?? 'active',
    });
    localStorage.setItem(keyPrefix(cid) + keyId + '@' + idx, value);
}

/** Get an encrypted private key from localStorage for an account. */
export function getEncryptedKey(cid: string, keyId: string): string | null {
    const prefix = keyPrefix(cid) + keyId + '@';
    for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
            const parsed = JSON.parse(localStorage.getItem(k)!);
            return parsed.enc;
        }
    }
    return null;
}

export interface StoredKeyEntry {
    keyId: string;
    encryptedData: string;
    algorithm: string;
    keyIndex: number;
    status: string;
}

/** Get all stored encrypted key entries for an account, with metadata. */
export function getStoredKeyEntries(cid: string): StoredKeyEntry[] {
    const prefix = keyPrefix(cid);
    const entries: StoredKeyEntry[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(prefix)) continue;
        const suffix = k.slice(prefix.length);
        const atIdx = suffix.lastIndexOf('@');
        // Storage key format is always {cid}_key_{keyId}@{keyIndex} → JSON
        // { enc, alg, idx, st }. Skip anything that doesn't match — entries
        // without an index suffix or with malformed JSON are corruption, not
        // legacy data (pre-prod, no migration).
        if (atIdx === -1) continue;
        const raw = localStorage.getItem(k);
        if (raw === null) continue;
        const parsed = JSON.parse(raw);
        entries.push({
            keyId: suffix.slice(0, atIdx),
            encryptedData: parsed.enc,
            algorithm: parsed.alg,
            keyIndex: parsed.idx ?? 0,
            status: parsed.st ?? 'active',
        });
    }
    return entries;
}

/** Get all stored encrypted key IDs for an account. */
export function getStoredKeyIds(cid: string): string[] {
    return getStoredKeyEntries(cid).map(e => e.keyId);
}

/** Encrypt a private key with the deviceEncryptionKey and store it for an account. */
export async function encryptAndStoreKey(
    cid: string,
    keyId: string,
    privateKeyBytes: Uint8Array,
    deviceEncryptionKey: Uint8Array,
    meta: { algorithm: string; keyIndex?: number; status?: string },
): Promise<void> {
    const nonce = aes.generateNonce();
    const ciphertext = await aes.encrypt(deviceEncryptionKey, nonce, privateKeyBytes);
    // Store as: base64(nonce) + ':' + base64(ciphertext)
    const stored = aes.toBase64(nonce) + ':' + aes.toBase64(ciphertext);
    storeEncryptedKey(cid, keyId, stored, meta);
}

/** Decrypt a stored key using the deviceEncryptionKey. Returns raw private key bytes. */
export async function decryptStoredKey(
    cid: string,
    keyId: string,
    deviceEncryptionKey: Uint8Array,
): Promise<Uint8Array | null> {
    const stored = getEncryptedKey(cid, keyId);
    if (!stored) return null;
    const [nonceB64, ctB64] = stored.split(':');
    if (!nonceB64 || !ctB64) return null;
    const nonce = aes.fromBase64(nonceB64);
    const ciphertext = aes.fromBase64(ctB64);
    return aes.decrypt(deviceEncryptionKey, nonce, ciphertext);
}

export interface DecryptedKeyEntry {
    keyId: string;
    privateKey: Uint8Array;
    algorithm: string;
    keyIndex: number;
    status: string;
}

/**
 * Thrown by {@link decryptAllStoredEntries} when a stored device-key copy cannot
 * be decrypted (or is malformed). The local material is stale or corrupt — e.g.
 * left over from a crypto migration, or encrypted under a superseded device key.
 * Callers must treat this as an unlock failure and fall back to supersecret
 * unlock, which re-derives the full bag from the server and rewrites device
 * storage — NOT load whatever subset happened to decrypt.
 */
export class StaleDeviceKeysError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'StaleDeviceKeysError';
    }
}

/** Decrypt all stored keys for an account with full metadata.
 *
 *  All-or-nothing: if ANY stored entry fails to decrypt or is malformed this
 *  throws {@link StaleDeviceKeysError} rather than returning a partial bag.
 *  A silently-partial bag is a footgun — a dropped `attr_encrypt` makes every
 *  claim render "[encrypted — keys not available]" with no error and no
 *  recovery path. Failing loudly lets the unlock flow fall back to supersecret
 *  unlock. */
export async function decryptAllStoredEntries(
    cid: string,
    deviceEncryptionKey: Uint8Array,
): Promise<DecryptedKeyEntry[]> {
    const stored = getStoredKeyEntries(cid);
    const result: DecryptedKeyEntry[] = [];
    for (const entry of stored) {
        const [nonceB64, ctB64] = entry.encryptedData.split(':');
        if (!nonceB64 || !ctB64) {
            throw new StaleDeviceKeysError(
                `Stored key ${entry.keyId}@${entry.keyIndex} is malformed (missing nonce/ciphertext).`,
            );
        }
        try {
            const nonce = aes.fromBase64(nonceB64);
            const ciphertext = aes.fromBase64(ctB64);
            const privateKey = await aes.decrypt(deviceEncryptionKey, nonce, ciphertext);
            result.push({
                keyId: entry.keyId,
                privateKey,
                algorithm: entry.algorithm,
                keyIndex: entry.keyIndex,
                status: entry.status,
            });
        } catch {
            throw new StaleDeviceKeysError(
                `Stored key ${entry.keyId}@${entry.keyIndex} failed to decrypt with the device key — ` +
                `device key material is stale or corrupt.`,
            );
        }
    }
    return result;
}

/** One (keyId, keyIndex) the server bag holds and this device's copy does not. */
export interface MissingDeviceEntry {
    keyId: string;
    keyIndex: number;
}

/**
 * Server bag entries this device's encrypted copy does NOT hold.
 *
 * The device copy is a CACHE of the server bag, written when the device is
 * enrolled (finalize-credentials `storeDeviceEntry`). Flows that append an
 * entry later — the A8b ceremony keystore, the derive-root store, the
 * key-grant client, the bitcash wallets group — write the server bag and the
 * in-memory bag only, and a key created on ANOTHER device never reaches this
 * copy at all. Device unlock reads only the copy, so without this check every
 * later unlock silently loads a bag missing those keys (live 2026-09-15: the
 * CPR ceremony pane reported no authority key while the server bag held it).
 *
 * Deliberately one-directional: an entry stored here that the server no
 * longer lists is NOT reported. The one flow that legitimately produces that
 * shape is a bag restored from a member-held backup after the server replica
 * was lost, and forcing a supersecret unlock there would fetch nothing better.
 * Revoked server entries DO count — chain-walking decryption of pre-rotation
 * data needs them (`keys.getKeyChain`). Order follows the server list.
 *
 * `manifest` is this device's key selection (device-setup-restructure §7.3).
 * A DECLINED id is absent on purpose and is never reported — without this the
 * selective-import feature would be a lockout, because every partial device
 * would be "stale" on every login (§12). A CHOSEN or UNDECIDED id must be
 * present, generation by generation: a rotated generation of a chosen key is
 * missing and imports at the next supersecret unlock (§1 item 11). Omit the
 * argument (or pass null) and this device's stored manifest is read — an
 * absent one means every key is chosen, which is a device set up before the
 * picker shipped.
 */
export function findEntriesMissingFromDevice(
    cid: string,
    serverEntries: readonly { keyId: string; keyIndex: number }[],
    manifest?: DeviceKeyManifest | null,
): MissingDeviceEntry[] {
    const selection = manifest ?? readDeviceKeyManifest(cid);
    const stored = new Set(getStoredKeyEntries(cid).map(e => `${e.keyId}@${e.keyIndex}`));
    const missing: MissingDeviceEntry[] = [];
    for (const e of serverEntries) {
        // Same predicate as every bag-to-device writer: a key the device is
        // barred from holding (`recovery_kit`, `bitcash_wallet`) can never be
        // caught up, so counting it made every unlock "stale" (2026-09-16).
        if (!isDeviceStorableKeyId(e.keyId)) continue;
        if (!isDeviceKeyChosen(selection, e.keyId)) continue;
        if (!stored.has(`${e.keyId}@${e.keyIndex}`)) {
            missing.push({ keyId: e.keyId, keyIndex: e.keyIndex });
        }
    }
    return missing;
}

/**
 * Delete this device's copy of one key — EVERY generation of it.
 *
 * What *untick* does in the Devices pane's "Keys on this device" group
 * (device-setup-restructure §9.3). Removal is free and needs no supersecret
 * (§1 item 13): the server bag still holds the key, and the manifest entry the
 * caller writes beside this call is what keeps the reconcile from reporting it
 * missing on the next unlock.
 *
 * Every generation goes, because the decision is per key ID and never per
 * generation (§1 item 11) — a device that kept the pre-rotation copy of a key
 * the member removed would still be holding that key.
 *
 * `keyId` may be a FAMILY glob (`agent:*:agent_signing_key`), which the
 * catalogue offers as one toggle: every agent's key goes with it. Entries
 * whose storage key does not carry the `@{keyIndex}` suffix are not key
 * entries and are never touched. (The use record lives at `{cid}_keyuse`,
 * outside this prefix altogether, so it is not even a candidate.)
 */
export function removeStoredKeyEntries(cid: string, keyId: string): void {
    const prefix = keyPrefix(cid);
    const isFamily = keyId.includes('*');
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const suffix = key.slice(prefix.length);
        const atIdx = suffix.lastIndexOf('@');
        if (atIdx === -1) continue;
        const storedId = suffix.slice(0, atIdx);
        if (storedId === keyId || (isFamily && matchesDeviceKeyFamily(keyId, storedId))) {
            toRemove.push(key);
        }
    }
    for (const key of toRemove) localStorage.removeItem(key);
}

/** Decrypt all stored keys for an account. Returns keyId → raw private key bytes.
 *  @deprecated Use decryptAllStoredEntries for full metadata support. */
export async function decryptAllStoredKeys(
    cid: string,
    deviceEncryptionKey: Uint8Array,
): Promise<Map<string, Uint8Array>> {
    const entries = await decryptAllStoredEntries(cid, deviceEncryptionKey);
    const result = new Map<string, Uint8Array>();
    for (const e of entries) result.set(e.keyId, e.privateKey);
    return result;
}

// ── Cleanup ──────────────────────────────────────────────────────────────────

/** Clear all stored keys, the encrypted device slots, the per-device key
 *  selection and the device token for an account — "Forget this device" in
 *  full.
 *
 *  The SLOTS go with the keys, deliberately: a de-enrolled browser that kept
 *  its cached `catalog:default` AUTH token would keep spending authority the
 *  member believes they took away, and the organization's stored voucher codes
 *  are money whose only other copy is on paper (registration-wallets D19, D26
 *  — the pane's copy says so before the member clicks). */
export function clearDeviceStorage(cid: string): void {
    const prefixes = [keyPrefix(cid), slotPrefix(cid)];
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && prefixes.some(p => key.startsWith(p))) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
    localStorage.removeItem(deviceTokenKey(cid));
    localStorage.removeItem(deviceIdKey(cid));
    localStorage.removeItem(pinLengthKey(cid));
    localStorage.removeItem(unlockFactorsKey(cid));
    localStorage.removeItem(noLocalStorageKey(cid));
    // The key selection goes with the keys it describes (§7.1): a re-enrolled
    // browser starts from the picker's defaults, not from a stale decision
    // about a bag it no longer holds.
    clearDeviceKeyManifest(cid);
    // So does the local evidence of what this device did with those keys
    // (§9.4). The record lives at `{cid}_keyuse`, deliberately OUTSIDE the
    // `{cid}_key_` prefix swept above (a prefix scan is what `hasStoredKeys`
    // does, and a record under it made an empty device look enrolled), so it
    // must be removed by name here.
    clearKeyUseRecord(cid);
}

/**
 * Every identity enrolled on this device — one that holds a device token, or
 * any device-encrypted private key, or both. A personal account and each of its
 * linked delegate accounts enrol separately on the same browser, so "Forget
 * this device" has to sweep all of them rather than only the active one.
 *
 * Only namespaced `{cid}_…` entries count. The globals the client stores under
 * a literal `cid_` prefix — `cid_active_cid`, `cid_key_grants_v1_{cid}`,
 * `cid_module_pubkeys_v1_{cid}` — must never be read as an identity called
 * "cid"; the key-entry shape (`{cid}_key_{keyId}@{index}`) and the explicit
 * prefix guard below both exclude them.
 */
export function listEnrolledIdentities(): string[] {
    const cids = new Set<string>();
    const tokenSuffix = '_device_token';
    // {cid}_key_{keyId}@{keyIndex} — the trailing @index is what tells a real
    // key entry apart from `cid_key_grants_v1_{cid}`.
    const keyEntry = /^(.+?)_key_.+@\d+$/;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || key.startsWith('cid_')) continue;
        if (key.endsWith(tokenSuffix)) {
            const cid = key.slice(0, -tokenSuffix.length);
            if (cid) cids.add(cid);
            continue;
        }
        const m = keyEntry.exec(key);
        if (m && m[1]) cids.add(m[1]);
    }
    return Array.from(cids);
}

/** Clear only the stored keys for an account (keep device token). */
export function clearStoredKeys(cid: string): void {
    const prefix = keyPrefix(cid);
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
}
